import React, { useEffect } from 'react';
import { ArrowRight, ShieldCheck, Beaker, Users, CheckCircle, Award, FileSearch } from 'lucide-react';
import SEO from './SEO';

interface Props {
  onNavigate: (view: any, sectionId?: string) => void;
}

const AboutUs: React.FC<Props> = ({ onNavigate }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-24 pb-16 bg-white">
      <SEO 
        title="About Us | BuildMySupplement Manufacturer" 
        description="Learn about our mission to democratize supplement manufacturing for creators. We are a GMP-certified, FDA-registered partner for your wellness brand." 
      />
      
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 tracking-tight mb-8 leading-tight">
            Leveling the Playing Field for the <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500">Next Generation</span> of Brands.
          </h1>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-3xl mx-auto">
            We aren't just a clinical factory pushing out white powders. We are a team of industry veterans on a mission to democratize premium supplement manufacturing for creators, influencers, and independent entrepreneurs.
          </p>
        </div>
      </section>

      {/* Our Story / Founder Letter */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-lg text-gray-700 space-y-8">
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">The Story Behind BuildMySupplement</h2>
          <p className="mb-4">
            Hi, I’m [Founder Name], the founder of BuildMySupplement.
          </p>
          <p className="mb-4">
            For over a decade, I worked deep inside the supplement industry. I managed supply chains, oversaw R&D for legacy brands, and walked the floors of massive manufacturing facilities. I saw firsthand what it takes to build a world-class wellness product. But I also saw the industry’s darkest, most exclusionary secret.
          </p>
          <p className="mb-4">
            The traditional manufacturing model was fundamentally designed to keep independent creators out.
          </p>
          <p className="mb-4">
            Legacy factories demanded Minimum Order Quantities (MOQs) of 10,000 to 50,000 units just to turn the blenders on. This dynamic meant that if you were a fitness influencer with a brilliant idea for a clean pre-workout, or a wellness coach wanting to launch an adaptogen blend for your community, you needed hundreds of thousands of dollars in venture capital just to test the waters. The gatekeepers held the keys, and innovation stagnated.
          </p>
          <p className="mb-4">
            I watched incredibly talented creators settle for 10% affiliate commissions, selling mediocre products for conglomerate brands because they couldn't afford to launch their own. That never sat right with me.
          </p>
          <p className="mb-4">
            In 2026, I launched BuildMySupplement to break those gates down. We built a nimble, state-of-the-art facility engineered specifically for the creator economy. By eliminating MOQs on our white label lines and drastically lowering barriers for custom private label formulations, we’ve made launching a premium supplement brand accessible to anyone with an audience and a vision.
          </p>
        </div>

        <div>
           <h2 className="text-3xl font-extrabold text-gray-900 mb-6">Our Mission & Expertise</h2>
           <p className="mb-4">
            Our mission is simple: <strong>We handle the science and the supply chain, so you can handle the community and the scale.</strong>
           </p>
           <p className="mb-4">
             But democratization doesn't mean compromising on quality. We operate strictly out of FDA-registered, GMP-certified facilities. Our internal team consists of veteran food scientists, flavor chemists, and compliance experts who ensure that every capsule, gummy, and scoop of powder that leaves our doors is safe, potent, and delicious. We treat your first 50-unit order with the exact same rigor and clinical testing as a 50,000-unit retail run.
           </p>
        </div>
      </section>

      {/* Why Trust Us Section */}
      <section className="bg-gray-50 py-20 mt-12 border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Why Trust Us With Your Brand?</h2>
            <p className="text-lg text-gray-600">In the health industry, E-E-A-T (Experience, Expertise, Authority, Trust) isn't just an SEO term; it's a moral imperative. Here is how we protect your customers and your reputation.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <ShieldCheck className="w-8 h-8 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">GMP-Certified</h3>
              <p className="text-gray-600 text-sm">We strictly adhere to Good Manufacturing Practices. This means rigorous sanitation, equipment calibration, and tightly controlled operating procedures that eliminate cross-contamination.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <Award className="w-8 h-8 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">FDA-Registered Facility</h3>
              <p className="text-gray-600 text-sm">Our facility is registered with and subject to inspection by the FDA, ensuring we meet all federal requirements for dietary supplement manufacturing.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <FileSearch className="w-8 h-8 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Third-Party Tested</h3>
              <p className="text-gray-600 text-sm">We don't grade our own homework. We utilize independent, ISO-accredited labs to verify the identity, purity, and potency of our raw materials and finished goods.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <CheckCircle className="w-8 h-8 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Transparent Sourcing</h3>
              <p className="text-gray-600 text-sm">We trace every ingredient back to its origin. Whether it's whey isolate from grass-fed cows or organic botanicals, we provide a clean, documented chain of custody.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <Users className="w-8 h-8 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Zero MOQ Commitment</h3>
              <p className="text-gray-600 text-sm">Trust is built by sharing risk. Our Zero MOQ white label program proves we believe in your potential to grow. We don't demand upfront capital to prove a partnership.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <Beaker className="w-8 h-8 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">In-House R&D Experts</h3>
              <p className="text-gray-600 text-sm">Our flavor chemists and formulation scientists have decades of combined experience. We don't outsource innovation; we engineer it in-house.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Success Stories</h2>
          <p className="text-lg text-gray-600">Hear from the creators and founders who turned their audience into a tangible wellness brand.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Testimonial 1 */}
          <div className="bg-orange-50 p-8 rounded-3xl">
             <div className="flex text-orange-500 mb-4">
                {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
             </div>
             <p className="text-gray-700 italic mb-6">"As a fitness coach, I wanted to launch a pre-workout but couldn't afford a $20k minimum order. BuildMySupplement let me launch my white label formula with just 100 units. We sold out in 48 hours, and they had my restock shipped seamlessly. Best partner ever."</p>
             <div className="font-bold text-gray-900">[Creator Name]</div>
             <div className="text-sm text-gray-500">Founder, [Brand Name]</div>
          </div>
          {/* Testimonial 2 */}
          <div className="bg-orange-50 p-8 rounded-3xl">
             <div className="flex text-orange-500 mb-4">
                {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
             </div>
             <p className="text-gray-700 italic mb-6">"We came to them for a custom private label vegan protein. Their flavor chemists nailed the texture and taste on the second iteration. The turnaround time from R&D to my warehouse was under 6 weeks. Incredible speed without sacrificing an ounce of quality."</p>
             <div className="font-bold text-gray-900">[Founder Name]</div>
             <div className="text-sm text-gray-500">CEO, [Brand Name]</div>
          </div>
          {/* Testimonial 3 */}
          <div className="bg-orange-50 p-8 rounded-3xl">
             <div className="flex text-orange-500 mb-4">
                {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
             </div>
             <p className="text-gray-700 italic mb-6">"I had a massive YouTube audience but was terrified of logistics. BMS literally handled everything—from ensuring my label claims were FDA compliant to drop-shipping the first batch."</p>
             <div className="font-bold text-gray-900">[Influencer Name]</div>
             <div className="text-sm text-gray-500">Wellness Creator, [Channel Name]</div>
          </div>
          {/* Testimonial 4 */}
          <div className="bg-orange-50 p-8 rounded-3xl">
             <div className="flex text-orange-500 mb-4">
                {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
             </div>
             <p className="text-gray-700 italic mb-6">"Our adaptogen gummy line is scaling rapidly. BuildMySupplement scales perfectly with us. The fact that they are GMP-certified gives me total peace of mind when talking to my customers."</p>
             <div className="font-bold text-gray-900">[Founder Name]</div>
             <div className="text-sm text-gray-500">Co-Founder, [Brand Name]</div>
          </div>
          {/* Testimonial 5 */}
          <div className="bg-orange-50 p-8 rounded-3xl lg:col-span-1 md:col-span-2 lg:col-start-2">
             <div className="flex text-orange-500 mb-4">
                {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
             </div>
             <p className="text-gray-700 italic mb-6">"I switched from a massive overseas manufacturer to BuildMySupplement. Communication is night and day. The zero MOQs on new SKU tests mean I can launch new flavors constantly. Revenue is up 40% this quarter."</p>
             <div className="font-bold text-gray-900">[Founder Name]</div>
             <div className="text-sm text-gray-500">Owner, [Brand Name]</div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white py-20 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-4xl font-extrabold mb-6">Ready to Build With Us?</h2>
          <p className="text-xl text-gray-400 mb-10">
            Let our experienced team formulate, manufacture, and scale your brand.
          </p>
          <button 
            onClick={() => onNavigate('contact')}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-black bg-orange-500 rounded-full hover:bg-orange-400 transition-all duration-200"
          >
            Start Your Project <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>

    </div>
  );
};

const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 fill-current" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
)

export default AboutUs;
